import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  Injector,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import {
  ActivityLogsApi,
  AiFieldContentGenerationsApi,
  AiFieldTranslationsApi,
  AiTextTranslationsApi,
  AuthenticationApi,
  AuthorizationApi,
  BulkUpdateProductActivitiesApi,
  ChannelsApi,
  CompaniesApi,
  Configuration,
  DiagnosticsApi,
  ExportsApi,
  GpcApi,
  GpcBricksApi,
  GpcClassesApi,
  GpcFamiliesApi,
  GpcSegmentsApi,
  GroupedProductMembersApi,
  ImportInputSchemasApi,
  ImportsApi,
  ModulesApi,
  ProductFeatureOptionsApi,
  ProductFeaturesApi,
  ProductsApi,
  ProductFieldConstraintsApi,
  ProductSchemasApi,
  ProductsFiltersApi,
  ProductsListsApi,
  ProductVariantsApi,
  SalesApi,
  SharingApi,
  SyncsApi,
  UsersApi,
} from './connectors/backend';
import {
  AiGenerationsApi,
  DamServicesApi,
  DigitalAssetCategoriesApi,
  DigitalAssetCollectionsApi,
  DigitalAssetTagsApi,
  FoldersApi,
} from './connectors/dam';
import { DigitalAssetsApi } from './connectors/dam/apis/DigitalAssetsApi';
import { Configuration as ConfigurationDam } from './connectors/dam/runtime';
import { provideLocaleId } from './providers/app-locale.provider';
import { provideCompany } from './providers/company.provider';
import { provideContentLocaleId } from './providers/content-locale.provider';
import { AgentPagesPreloading } from './agent/agent-route-paths';
import { appPreset } from './theme/app-theme';
import {
  fetchWithDeadline,
  watchApiConnectivity,
} from './utils/api-connectivity';
import { logoutOnUnauthorized } from './utils/logout-on-unauthorized';
import { salesAccessLost } from './utils/sales-access-lost';

export const appConfig: ApplicationConfig = {
  providers: [
    ConfirmationService,
    MessageService,
    DialogService,
    provideLocaleId(),
    provideCompany(),
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    provideContentLocaleId(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withPreloading(AgentPagesPreloading),
    ),
    provideHttpClient(),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            // Well above `staleTime`: with the default (also 5 minutes) an entry
            // was evicted as soon as it went stale with no observer left, so
            // returning to a page paid a cold fetch instead of a background
            // refresh over cached data.
            gcTime: 30 * 60 * 1000, // 30 minutes
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
          },
        },
      }),
    ),
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'dd MMM yyyy, HH:mm' },
    },
    providePrimeNG({
      theme: {
        preset: appPreset,
        options: {
          darkModeSelector: '.theme-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng',
          },
        },
      },
      translation: {
        dateFormat: 'dd MMM yyyy, HH:mm',
        selectionMessage: $localize`{0} elementi selezionati`,
        emptySelectionMessage: $localize`Nessun elemento selezionato`,
        emptyMessage: $localize`Nessun risultato trovato`,
        emptySearchMessage: $localize`Nessun risultato trovato`,
        emptyFilterMessage: $localize`Nessun risultato trovato`,
      },
    }),
    // Backend API
    {
      provide: Configuration,
      useFactory: (injector: Injector) =>
        new Configuration({
          basePath: environment.baseApiPath,
          middleware: [logoutOnUnauthorized(injector)],
        }),
      deps: [Injector],
    },
    {
      provide: AiTextTranslationsApi,
      useFactory: (config: Configuration) => new AiTextTranslationsApi(config),
      deps: [Configuration],
    },
    {
      provide: DiagnosticsApi,
      useFactory: (config: Configuration) => new DiagnosticsApi(config),
      deps: [Configuration],
    },
    {
      provide: AuthenticationApi,
      useFactory: (config: Configuration) => new AuthenticationApi(config),
      deps: [Configuration],
    },
    {
      provide: AuthorizationApi,
      useFactory: (config: Configuration) => new AuthorizationApi(config),
      deps: [Configuration],
    },
    {
      provide: UsersApi,
      useFactory: (config: Configuration) => new UsersApi(config),
      deps: [Configuration],
    },
    {
      provide: ProductsApi,
      useFactory: (config: Configuration) => new ProductsApi(config),
      deps: [Configuration],
    },
    {
      provide: ProductsListsApi,
      useFactory: (config: Configuration) => new ProductsListsApi(config),
      deps: [Configuration],
    },
    {
      provide: ProductsFiltersApi,
      useFactory: (config: Configuration) => new ProductsFiltersApi(config),
      deps: [Configuration],
    },
    {
      provide: ProductFieldConstraintsApi,
      useFactory: (config: Configuration) =>
        new ProductFieldConstraintsApi(config),
      deps: [Configuration],
    },
    {
      provide: ProductSchemasApi,
      useFactory: (config: Configuration) => new ProductSchemasApi(config),
      deps: [Configuration],
    },
    {
      provide: ProductVariantsApi,
      useFactory: (config: Configuration) => new ProductVariantsApi(config),
      deps: [Configuration],
    },
    {
      provide: GroupedProductMembersApi,
      useFactory: (config: Configuration) =>
        new GroupedProductMembersApi(config),
      deps: [Configuration],
    },
    {
      provide: CompaniesApi,
      useFactory: (config: Configuration) => new CompaniesApi(config),
      deps: [Configuration],
    },
    {
      provide: DigitalAssetTagsApi,
      useFactory: (config: ConfigurationDam) => new DigitalAssetTagsApi(config),
      deps: [ConfigurationDam],
    },
    {
      provide: ProductFeaturesApi,
      useFactory: (config: Configuration) => new ProductFeaturesApi(config),
      deps: [Configuration],
    },
    {
      provide: ProductFeatureOptionsApi,
      useFactory: (config: Configuration) =>
        new ProductFeatureOptionsApi(config),
      deps: [Configuration],
    },
    {
      provide: ActivityLogsApi,
      useFactory: (config: Configuration) => new ActivityLogsApi(config),
      deps: [Configuration],
    },
    {
      provide: SharingApi,
      useFactory: (config: Configuration) => new SharingApi(config),
      deps: [Configuration],
    },
    {
      provide: BulkUpdateProductActivitiesApi,
      useFactory: (config: Configuration) =>
        new BulkUpdateProductActivitiesApi(config),
      deps: [Configuration],
    },
    {
      provide: SyncsApi,
      useFactory: (config: Configuration) => new SyncsApi(config),
      deps: [Configuration],
    },
    {
      provide: ChannelsApi,
      useFactory: (config: Configuration) => new ChannelsApi(config),
      deps: [Configuration],
    },
    {
      provide: GpcApi,
      useFactory: (config: Configuration) => new GpcApi(config),
      deps: [Configuration],
    },
    {
      provide: GpcSegmentsApi,
      useFactory: (config: Configuration) => new GpcSegmentsApi(config),
      deps: [Configuration],
    },
    {
      provide: GpcFamiliesApi,
      useFactory: (config: Configuration) => new GpcFamiliesApi(config),
      deps: [Configuration],
    },
    {
      provide: GpcClassesApi,
      useFactory: (config: Configuration) => new GpcClassesApi(config),
      deps: [Configuration],
    },
    {
      provide: GpcBricksApi,
      useFactory: (config: Configuration) => new GpcBricksApi(config),
      deps: [Configuration],
    },
    // DAM API
    {
      provide: ConfigurationDam,
      useFactory: (injector: Injector) =>
        new ConfigurationDam({
          basePath: environment.baseApiPath,
          middleware: [logoutOnUnauthorized(injector)],
        }),
      deps: [Injector],
    },
    {
      provide: DigitalAssetsApi,
      useFactory: (config: ConfigurationDam) => new DigitalAssetsApi(config),
      deps: [ConfigurationDam],
    },
    {
      provide: DamServicesApi,
      useFactory: (config: ConfigurationDam) => new DamServicesApi(config),
      deps: [ConfigurationDam],
    },
    {
      provide: AiGenerationsApi,
      useFactory: (config: ConfigurationDam) => new AiGenerationsApi(config),
      deps: [ConfigurationDam],
    },
    {
      provide: ImportsApi,
      useFactory: (config: Configuration) => new ImportsApi(config),
      deps: [Configuration],
    },
    {
      provide: ImportInputSchemasApi,
      useFactory: (config: Configuration) => new ImportInputSchemasApi(config),
      deps: [Configuration],
    },
    {
      provide: ExportsApi,
      useFactory: (config: Configuration) => new ExportsApi(config),
      deps: [Configuration],
    },
    {
      provide: DigitalAssetCategoriesApi,
      useFactory: (config: ConfigurationDam) =>
        new DigitalAssetCategoriesApi(config),
      deps: [ConfigurationDam],
    },
    {
      provide: AiFieldContentGenerationsApi,
      useFactory: (config: Configuration) =>
        new AiFieldContentGenerationsApi(config),
      deps: [Configuration],
    },
    {
      provide: FoldersApi,
      useFactory: (config: ConfigurationDam) => new FoldersApi(config),
      deps: [ConfigurationDam],
    },
    {
      provide: DigitalAssetCollectionsApi,
      useFactory: (config: ConfigurationDam) =>
        new DigitalAssetCollectionsApi(config),
      deps: [ConfigurationDam],
    },
    {
      provide: AiFieldTranslationsApi,
      useFactory: (config: Configuration) => new AiFieldTranslationsApi(config),
      deps: [Configuration],
    },
    {
      provide: ModulesApi,
      useFactory: (config: Configuration) => new ModulesApi(config),
      deps: [Configuration],
    },
    {
      provide: SalesApi,
      useFactory: (injector: Injector) =>
        new SalesApi(
          new Configuration({
            basePath: environment.baseApiPath,
            fetchApi: fetchWithDeadline,
            middleware: [
              watchApiConnectivity,
              logoutOnUnauthorized(injector),
              salesAccessLost(injector),
            ],
          }),
        ),
      deps: [Injector],
    },
  ],
};
